'use client';

import { Icon, Textarea, Flex } from '@/_components';
import styles from './QuestionFormTextarea.module.css';
import { useEffect, useRef, useContext, useState, type FC } from 'react';
import { ClientQuestionnaireContext } from '@/_contexts/client/ClientQuestionnaireProvider';
import { useDebounce } from '@/_hooks/useDebounce';
import { PreviewContext } from '@/_contexts/PreviewProvider';
import { usePrevious } from '@/_hooks/usePrevious';
import { getClasses } from '@/_utils/classnames';
import { UserPreviewContext } from '@/_contexts/admin/UserPreviewProvider';
import type { QuestionFormTextareaProps } from './QuestionFormTextarea.types';
import { SAVE_TEXT_DELAY_MS } from '@/_constants/questions';

export const QuestionFormTextarea: FC<QuestionFormTextareaProps> = ({
  question,
  isDisabled,
  mode,
}) => {
  const { updateQuestion } = useContext(ClientQuestionnaireContext);
  const { updatePreviewQuestion } = useContext(PreviewContext);
  const { updateTemplateQuestion } = useContext(UserPreviewContext);

  const previousQuestionId: number = usePrevious(question.id);

  const [text, setText] = useState<string>(question?.answer || '');
  const [isMounted, setMounted] = useState<boolean>(false);
  const [isSaved, setSaved] = useState<boolean>(false);
  const [isBlurred, setBlurred] = useState<boolean>(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    if (question?.id !== previousQuestionId) {
      setMounted(false);
      setText(question?.answer || '');
    }

    setTimeout(() => setMounted(true), 300);

    return () => setMounted(false);
  }, [question?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const debouncedText: string = useDebounce<string>(text, SAVE_TEXT_DELAY_MS);

  useEffect(() => {
    const onSave = () => {
      const answerParams = { questionId: question.id, answer: text };

      if (mode === 'admin-preview') return updatePreviewQuestion(answerParams);
      if (mode === 'admin-template') {
        return updateTemplateQuestion(answerParams);
      }

      return updateQuestion(answerParams);
    };

    const shouldSave = isMounted && text !== question.answer;

    shouldSave && setSaved(true);
    shouldSave && onSave();

    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => setSaved(false), 1000);

    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [debouncedText, isBlurred]); // eslint-disable-line react-hooks/exhaustive-deps

  const onTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    event.preventDefault();
    setText(event.target.value);
  };

  const onBlur = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    event.preventDefault();
    setBlurred(true);
    onTextChange(event);
  };

  const iconClasses: string = getClasses(styles.questionFormTextareaIcon, {
    [styles.questionFormTextareaIconSaved]: isSaved,
  });

  return (
    <Flex isColumn={true} className={styles.questionFormTextarea}>
      <Icon
        className={iconClasses}
        name={isSaved ? 'check-circle-filled' : 'check-circle'}
        size={20}
      />
      <Textarea
        label="Answer"
        isLabelHidden={true}
        rows={4}
        isDisabled={isDisabled || !isMounted}
        placeholder="Your Answer"
        value={text}
        onBlur={onBlur}
        onChange={onTextChange}
        onFocus={() => setBlurred(false)}
      />
    </Flex>
  );
};
